import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { Search, Home, Plus } from 'lucide-react'
import type { PropertyListItem } from '@natalan/capehost-contracts'
import { useProperties } from '@/hooks/useApi'
import { Input } from '@/components/ui/input'
import { Card, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { PropertiesEmptyState } from '@/components/properties-empty-state'
import { AuthorizationError } from '@/api/client'
import { getPropertyPhotoUrl } from '@/lib/format-utils'
import { isOnboardingProperty } from '@/lib/property-lifecycle'
import { StartOnboardingDialog } from '@/components/onboarding/start-onboarding-dialog'

type PropertiesTab = 'active' | 'archived'

// The admin-api property-list response only strictly guarantees the fields
// modeled by PropertyListItem, but the underlying KV record it's built from
// (see @natalan/capehost-contracts' PropertySchema doc comment: "there is no
// canonical writer schema... deliberately loose... a real record can carry
// additional sync-pipeline-owned fields") can carry more. This widens the
// type the same way format-utils.ts's own PropertyLike already does for its
// property helpers, scoped to the extra fields this page's free-text search
// and mobile location line read. `address` is left as `unknown` rather than
// guessed as a union, since the code below itself isn't sure whether it's a
// string or an object (see the typeof check a few lines down) and the list
// endpoint's real response/mock never actually populates it either way.
interface PropertyExternalAttributes {
  nickname?: string
  name?: string
  time_zone?: string
  property_slug?: string
  type?: string
  city?: string
  state?: string
  location?: string
}

interface PropertyRow extends PropertyListItem {
  timezone?: string
  property_slug?: string
  address?: unknown
  archived?: boolean
  managed_until?: string | null
  external?: {
    uplisting?: {
      attributes?: PropertyExternalAttributes
    }
  }
}

/**
 * Convert a property object into a searchable string
 * Searches across id, name, display_name, nickname, timezone, address fields, and other attributes
 */
function propertyToSearchableString(property: PropertyRow | null | undefined): string {
  if (!property || typeof property !== 'object') return ''

  const parts: string[] = []

  // Helper to safely add value to parts
  const addValue = (value: unknown): void => {
    if (value === null || value === undefined) return
    if (typeof value === 'string' || typeof value === 'number') {
      parts.push(String(value))
    } else if (typeof value === 'object' && !Array.isArray(value)) {
      // Flatten nested objects one level deep (e.g., address.*, external.uplisting.attributes.*)
      Object.values(value as Record<string, unknown>).forEach(nestedValue => {
        if (typeof nestedValue === 'string' || typeof nestedValue === 'number') {
          parts.push(String(nestedValue))
        } else if (typeof nestedValue === 'object' && !Array.isArray(nestedValue)) {
          // Go one more level deep for external.uplisting.attributes
          Object.values((nestedValue as Record<string, unknown>) || {}).forEach(deepValue => {
            if (typeof deepValue === 'string' || typeof deepValue === 'number') {
              parts.push(String(deepValue))
            }
          })
        }
      })
    }
  }

  // Add key searchable fields explicitly
  if (property.property_id) parts.push(String(property.property_id))
  if (property.id) parts.push(String(property.id))
  if (property.name) parts.push(String(property.name))
  if (property.display_name) parts.push(String(property.display_name))
  if (property.timezone) parts.push(String(property.timezone))
  if (property.property_slug) parts.push(String(property.property_slug))

  // Add nested fields
  if (property.address) {
    if (typeof property.address === 'string') {
      parts.push(property.address)
    } else {
      addValue(property.address)
    }
  }

  // Add from external.uplisting.attributes (for nickname, name, etc.)
  if (property.external?.uplisting?.attributes) {
    const attrs = property.external.uplisting.attributes
    if (attrs.nickname) parts.push(String(attrs.nickname))
    if (attrs.name) parts.push(String(attrs.name))
    if (attrs.time_zone) parts.push(String(attrs.time_zone))
    if (attrs.property_slug) parts.push(String(attrs.property_slug))
    if (attrs.type) parts.push(String(attrs.type))
    // Add city/location if available in attributes
    if (attrs.city) parts.push(String(attrs.city))
    if (attrs.location) parts.push(String(attrs.location))
  }

  // Add all top-level fields for completeness
  Object.values(property).forEach(addValue)

  return parts.join(' ').toLowerCase()
}

/**
 * Filter properties by search query
 * Supports multi-token search where all tokens must match
 */
function filterProperties(properties: PropertyRow[], query: string): PropertyRow[] {
  if (!query.trim()) return properties

  const tokens = query
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(token => token.length > 0)

  if (tokens.length === 0) return properties

  return properties.filter(property => {
    const searchableString = propertyToSearchableString(property)
    // All tokens must be found in the searchable string
    return tokens.every(token => searchableString.includes(token))
  })
}

/**
 * Custom hook for debounced value
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

/** Marks a property that is still onboarding (no Uplisting listing yet). */
function OnboardingPill() {
  return (
    <Badge variant="warning" className="shrink-0">
      Onboarding
    </Badge>
  )
}

export function PropertiesPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // Tab lives in the URL (?tab=archived) so it's reachable/bookmarkable,
  // mirroring the ?tab= pattern already used by property-details.tsx.
  const activeTab: PropertiesTab = searchParams.get('tab') === 'archived' ? 'archived' : 'active'
  const { data, isLoading, error, refetch } = useProperties(activeTab)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [startOpen, setStartOpen] = useState(false)

  // Initialize search query from URL
  const [searchQuery, setSearchQuery] = useState(() => {
    return searchParams.get('q') || ''
  })

  // Debounce search query (250ms)
  const debouncedSearchQuery = useDebounce(searchQuery, 250)

  // Update URL when debounced query changes. Uses the functional updater form
  // (merge into whatever params currently exist) rather than replacing the
  // whole query string, so this doesn't clobber `tab` when the operator is
  // searching the Archived tab.
  useEffect(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (debouncedSearchQuery.trim()) {
          next.set('q', debouncedSearchQuery)
        } else {
          next.delete('q')
        }
        return next
      },
      { replace: true }
    )
  }, [debouncedSearchQuery, setSearchParams])

  const handleTabChange = useCallback(
    (value: string) => {
      const nextTab: PropertiesTab = value === 'archived' ? 'archived' : 'active'
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if (nextTab === 'archived') {
            next.set('tab', 'archived')
          } else {
            next.delete('tab')
          }
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  // Focus search input when properties are loaded, unless the Start
  // onboarding dialog is open: it refreshes the list behind itself, and
  // moving focus behind a modal would fight its focus trap. Read through a
  // ref so closing the dialog does not itself pull focus to the search.
  const startOpenRef = useRef(startOpen)
  useEffect(() => {
    startOpenRef.current = startOpen
  }, [startOpen])
  useEffect(() => {
    if (startOpenRef.current) return
    if (!isLoading && data?.properties && data.properties.length > 0 && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isLoading, data?.properties])

  const filteredProperties = useMemo(() => {
    if (!data?.properties) return []
    // `external`'s real (but unmodeled-by-contract, see PropertyRow above)
    // shape is only known to this page, not the published schema, which
    // validates it as `unknown` -- narrow with a cast rather than widening
    // the schema itself to a shape only this one page's search cares about.
    return filterProperties(data.properties as PropertyRow[], debouncedSearchQuery)
  }, [data, debouncedSearchQuery])

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }, [])

  // The tab control itself never depends on loading/error state -- an
  // operator switching from Active to Archived while Active is still
  // loading (or errored) should always be able to, rather than the tabs
  // disappearing along with the content below them.
  const tabsBar = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="archived">Archived</TabsTrigger>
        </TabsList>
      </Tabs>
      {/* Active tab only: that is where the new property appears. */}
      {activeTab === 'active' && (
        <Button size="sm" onClick={() => setStartOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Start onboarding
        </Button>
      )}
      <StartOnboardingDialog open={startOpen} onOpenChange={setStartOpen} />
    </div>
  )

  if (isLoading) {
    return (
      <div className="space-y-4">
        {tabsBar}
        <div className="flex gap-4">
          <LoadingSkeleton className="h-10 w-full max-w-lg" />
        </div>
        {/* Mobile: List skeleton (Airbnb-style) */}
        <div className="block md:hidden space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex gap-3">
              <LoadingSkeleton className="h-20 w-20 rounded-md shrink-0" />
              <div className="flex-1 space-y-2">
                <LoadingSkeleton className="h-4 w-3/4" />
                <LoadingSkeleton className="h-3 w-1/2" />
                <LoadingSkeleton className="h-3 w-2/3" />
              </div>
            </div>
          ))}
        </div>
        {/* Desktop: Card grid skeleton */}
        <div className="hidden md:grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Card key={i} className="overflow-hidden">
              <LoadingSkeleton className="aspect-4/3 w-full" />
              <div className="space-y-2 p-3">
                <LoadingSkeleton className="h-4 w-3/4" />
                <LoadingSkeleton className="h-3 w-1/2" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    // Check if it's an authorization error (401/403)
    const isAuthorizationError = error instanceof AuthorizationError
    return (
      <div className="space-y-4">
        {tabsBar}
        <ErrorState error={error} onRetry={isAuthorizationError ? undefined : refetch} />
      </div>
    )
  }

  const hasProperties = data?.properties && data.properties.length > 0
  // Independent of the search query: a genuinely empty tab (e.g. no archived
  // properties at all) must show its own empty state even when a query
  // string carries over from a different tab (?q= persists across tab
  // switches) -- otherwise isEmpty and hasNoMatches were both false and
  // nothing rendered at all (Codex PR review round 2).
  const isEmpty = !hasProperties
  const hasNoMatches = hasProperties && filteredProperties.length === 0

  return (
    <div className="space-y-4">
      {tabsBar}

      {/* Show search only when there are properties */}
      {hasProperties && (
        <div className="relative w-full max-w-lg">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textMuted" />
          <Input
            ref={searchInputRef}
            placeholder="Search properties..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="pl-9"
          />
        </div>
      )}

      {isEmpty ? (
        activeTab === 'archived' ? (
          <EmptyState message="No archived properties. Properties appear here once they're archived from their Settings tab." />
        ) : (
          <PropertiesEmptyState onRefresh={refetch} />
        )
      ) : hasNoMatches ? (
        <EmptyState message="No properties match your search" />
      ) : (
        <>
          {/* Mobile: List view (Airbnb-style) */}
          <div className="block md:hidden space-y-0">
            {filteredProperties.map(property => {
              const displayName =
                property.display_name ||
                property.name ||
                `Property ${property.property_id || property.id}`
              const onboarding = isOnboardingProperty(property)
              const photoUrl = onboarding
                ? null
                : getPropertyPhotoUrl({
                    cover_photo_url: property.cover_photo_url ?? undefined,
                    external: property.external,
                    display_name: property.display_name,
                    name: property.name,
                  })
              const propertyId = property.property_id || property.id

              // Extract location info if available
              const attrs: PropertyExternalAttributes =
                property.external?.uplisting?.attributes || {}
              const addressObj = property.address as
                { city?: string; state?: string } | string | undefined
              const addressCity = typeof addressObj === 'object' ? addressObj?.city : undefined
              const addressState = typeof addressObj === 'object' ? addressObj?.state : undefined
              const location =
                attrs.city && attrs.state
                  ? `${attrs.city}, ${attrs.state}`
                  : attrs.city || attrs.state || addressCity || addressState || null
              const locationLine = location ? `Home in ${location}` : null

              return (
                <div
                  key={propertyId}
                  className={`flex gap-3 px-4 py-3 border-b border-border cursor-pointer hover:bg-surface2 transition-colors ${
                    property.archived ? 'opacity-60' : ''
                  }`}
                  onClick={() => navigate(`/properties/${propertyId}`)}
                >
                  {/* Thumbnail image - left side. While onboarding the
                      property has no listing photos, so the tile keeps the
                      list aligned with the house placeholder alone. */}
                  <div className="relative h-20 w-20 shrink-0 rounded-md overflow-hidden bg-surface2">
                    {photoUrl ? (
                      <img
                        src={photoUrl}
                        alt={displayName}
                        loading="lazy"
                        className="h-full w-full object-cover"
                        onError={e => {
                          e.currentTarget.style.display = 'none'
                          const placeholder = e.currentTarget.nextElementSibling
                          if (placeholder instanceof HTMLElement) {
                            placeholder.style.display = 'flex'
                          }
                        }}
                      />
                    ) : null}
                    {/* Placeholder when no photo or on error */}
                    <div
                      className={`flex h-full w-full items-center justify-center bg-linear-to-br from-surface2 to-surface ${
                        photoUrl ? 'hidden' : 'flex'
                      }`}
                    >
                      <Home className="h-6 w-6 text-textFaint" />
                    </div>
                  </div>

                  {/* Text content - right side */}
                  <div className="flex-1 min-w-0">
                    {/* Line 1: Property title (bold, prominent) */}
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-semibold text-sm leading-tight line-clamp-2">
                        {displayName}
                      </div>
                      {property.archived && (
                        <Badge variant="secondary" className="shrink-0">
                          Archived
                        </Badge>
                      )}
                      {onboarding && <OnboardingPill />}
                    </div>
                    {/* Line 2: ID (shown as property identifier) */}
                    <div className="text-xs text-textMuted font-mono mb-1">ID: {propertyId}</div>
                    {/* Line 3: Location (if available) */}
                    {locationLine && <div className="text-xs text-textMuted">{locationLine}</div>}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop: Card grid view */}
          <div className="hidden md:grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredProperties.map(property => {
              // Get property display name (prefer display_name from API, fallback to name)
              const displayName =
                property.display_name ||
                property.name ||
                `Property ${property.property_id || property.id}`

              const propertyId = property.property_id || property.id
              const onboarding = isOnboardingProperty(property)

              // Get photo URL (from API cover_photo_url or fallback); none
              // while onboarding, when the card has no photo frame.
              const photoUrl = onboarding
                ? null
                : getPropertyPhotoUrl({
                    cover_photo_url: property.cover_photo_url ?? undefined,
                    external: property.external,
                    display_name: property.display_name,
                    name: property.name,
                  })

              return (
                <Card
                  key={propertyId}
                  className={`group cursor-pointer overflow-hidden border border-border bg-surface transition-all duration-200 hover:border-borderStrong hover:shadow-lg ${
                    property.archived ? 'opacity-60' : ''
                  }`}
                  onClick={() => navigate(`/properties/${propertyId}`)}
                >
                  {/* Photo Section - Smaller (about 2/3 of current). An
                      onboarding property has no photos yet, so its card is
                      the text block alone, with no empty photo frame. */}
                  {!onboarding && (
                    <div className="relative aspect-4/3 w-full overflow-hidden rounded-t-card bg-surface2">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={displayName}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={e => {
                            // Hide image and show placeholder on error
                            e.currentTarget.style.display = 'none'
                            const placeholder = e.currentTarget.nextElementSibling
                            if (placeholder instanceof HTMLElement) {
                              placeholder.style.display = 'flex'
                            }
                          }}
                        />
                      ) : null}
                      {/* Placeholder when no photo or on error */}
                      <div
                        className={`flex h-full w-full flex-col items-center justify-center bg-linear-to-br from-surface2 to-surface ${
                          photoUrl ? 'hidden' : 'flex'
                        }`}
                      >
                        <Home className="mb-1.5 h-8 w-8 text-textFaint" />
                        <span className="text-xs text-textFaint">No photo</span>
                      </div>
                    </div>
                  )}

                  {/* Content Section - Compact */}
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      <CardTitle className="line-clamp-2 text-sm font-semibold leading-tight">
                        {displayName}
                      </CardTitle>
                      {property.archived && (
                        <Badge variant="secondary" className="shrink-0">
                          Archived
                        </Badge>
                      )}
                      {onboarding && <OnboardingPill />}
                    </div>
                    <div className="mt-1.5">
                      <span className="font-mono text-xs text-textMuted">ID: {propertyId}</span>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
